import { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { theme } from "../lib/theme";
import { useNetworks, useAnalysisStatus } from "../api/hooks";
import { useNetworkStore } from "../stores/network";
import { useNetworkViews } from "../stores/saved-views";
import { Select } from "./ui";
import { ClustecLogo } from "./Logo";
import { TxClipboard } from "./TxClipboard";
import { useWebSocket } from "../hooks/useWebSocket";
import { Search } from "./Search";

/**
 * Owns the viewport, so the space below the search bar is a known quantity and `Main` can hand it
 * out. Previously this was `min-height`, which is a floor and not a ceiling: a page asking for
 * `100vh` got the viewport *plus* the bar above it and the whole app scrolled by the difference.
 * `dvh` keeps that honest on mobile, where the browser's own chrome comes and goes.
 */
const Shell = styled.div`
  display: flex;
  height: 100vh;
  height: 100dvh;
`;

const Overlay = styled.div<{ visible: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${(p) => (p.visible ? "block" : "none")};
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 90;
  }
`;

const Sidebar = styled.nav<{ open: boolean }>`
  width: 220px;
  background: ${theme.colors.bgCard};
  border-right: 1px solid ${theme.colors.border};
  padding: ${theme.spacing.lg} 0;
  flex-shrink: 0;
  /* The shell is now exactly the viewport, so the nav scrolls itself rather than being clipped on a
     short window. */
  overflow-y: auto;

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 100;
    transform: ${(p) => (p.open ? "translateX(0)" : "translateX(-100%)")};
    transition: transform 0.2s ease;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: 0 ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const LogoText = styled.span`
  font-size: ${theme.fontSize.lg};
  font-weight: 800;
  color: ${theme.colors.primary};
  margin-left: 5px;
  letter-spacing: -0.5px;
`;

const NavItem = styled(Link, { shouldForwardProp: (prop) => prop !== "active" })<{ active?: boolean }>`
  display: block;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  color: ${(p) => (p.active ? theme.colors.text : theme.colors.textMuted)};
  text-decoration: none;
  font-size: ${theme.fontSize.sm};
  font-weight: ${(p) => (p.active ? 600 : 400)};
  background: ${(p) => (p.active ? theme.colors.bgHover : "transparent")};
  border-left: 3px solid
    ${(p) => (p.active ? theme.colors.primary : "transparent")};
  /* Saved-view names are user-chosen and can be long — one line, cut off. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${theme.colors.bgHover};
    color: ${theme.colors.text};
  }
`;

const NavGroup = styled.div`
  margin-bottom: ${theme.spacing.md};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const NavHeading = styled.div`
  padding: 0 ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xs};
  font-size: ${theme.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
`;

const NetworkSelector = styled.div`
  padding: 0 ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  position: relative;
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const AnalyzingDot = styled.div`
  position: absolute;
  top: -2px;
  right: 14px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${theme.colors.accent};
  animation: ${pulse} 2s ease-in-out infinite;
`;

/** Desktop search bar. Hidden on mobile, where search lives in the header as an overlay. */
const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  border-bottom: 1px solid ${theme.colors.border};

  @media (max-width: 768px) {
    display: none;
  }
`;

/**
 * The scroll container, and the thing that measures out the height under the header bars.
 *
 * A grid rather than a flex column on purpose: the second row is the page, and `1fr` gives it the
 * leftover when its content fits while still growing past it when it doesn't. A flex column would
 * instead shrink a long page below its content, since flex items shrink by default — which would
 * squash every table in the app to fit one screen.
 *
 * Two rows, not three: `MobileHeader` and `TopBar` are mutually exclusive at the same breakpoint and
 * the hidden one is not a grid item at all, so exactly one header row exists at any width. Adding a
 * header that shows alongside them means adding a row here — otherwise it takes the page's `1fr`
 * and the page silently stops filling the screen.
 *
 * The column is `minmax(0, 1fr)` and not the implicit `auto`, which is what makes narrow screens
 * work. An `auto` track is floored by its item's min-content width, so one wide row — a table, a
 * nowrap toolbar — stretched the whole page to that width. Everything inside then measured itself
 * against the stretched page instead of the screen, so a table's `overflow-x` wrapper came out wider
 * than its table and had nothing left to scroll, while `overflow-x: hidden` here quietly cut off the
 * part that did not fit. `minmax(0, 1fr)` holds the page at the screen's width, which puts the
 * overflow back inside the wrappers built to scroll it.
 */
const Main = styled.main`
  flex: 1;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: grid;
  grid-template-rows: auto 1fr;
  grid-template-columns: minmax(0, 1fr);
`;

const MobileHeader = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    background: ${theme.colors.bgCard};
    border-bottom: 1px solid ${theme.colors.border};
    position: sticky;
    top: 0;
    z-index: 50;
  }
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text};
  font-size: 24px;
  cursor: pointer;
  padding: ${theme.spacing.xs};
  line-height: 1;
`;

const MobileHeaderSpacer = styled.div`
  margin-left: auto;
`;

const MobileLogo = styled.span`
  font-size: ${theme.fontSize.md};
  font-weight: 800;
  color: ${theme.colors.primary};
  letter-spacing: -0.5px;
`;

// Grouped rather than flat: the list is now long enough that "where do I find sequencer
// reliability" is a real question, and the groups answer it — chain-level views, privacy
// analysis, then management.
const navGroups = [
  {
    heading: "Chain",
    items: [
      { path: "/", label: "Dashboard" },
      { path: "/health", label: "Health" },
      { path: "/blocks", label: "Blocks" },
      { path: "/fees", label: "Fees" },
    ],
  },
  {
    heading: "Privacy",
    items: [
      { path: "/txs", label: "Transactions" },
      { path: "/privacy-sets", label: "Privacy Sets" },
      { path: "/murder-board", label: "Murder Board" },
    ],
  },
  {
    heading: "Manage",
    items: [
      { path: "/labels", label: "Labels" },
      { path: "/admin", label: "Admin" },
    ],
  },
];

export function Layout() {
  useWebSocket();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ network?: string }>();
  const { data: networks } = useNetworks();
  const { selectedNetwork, setNetwork } = useNetworkStore();
  const { data: analysisStatus } = useAnalysisStatus(selectedNetwork);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const savedViews = useNetworkViews(selectedNetwork);

  // Saved views are the user's own shortcuts, so they get their own group
  // right under the privacy tools they're built from — and only once one exists.
  const afterPrivacy = navGroups.findIndex((g) => g.heading === "Privacy") + 1;
  const groups =
    savedViews.length === 0
      ? navGroups
      : [
          ...navGroups.slice(0, afterPrivacy),
          {
            heading: "Saved views",
            items: savedViews.map((v) => ({ path: `/views/${v.id}`, label: v.name })),
          },
          ...navGroups.slice(afterPrivacy),
        ];

  // Sync network from URL path param, redirect if invalid
  useEffect(() => {
    if (!networks || networks.length === 0) return;
    const validIds = new Set(networks.map((n) => n.id));

    if (params.network && validIds.has(params.network)) {
      if (params.network !== selectedNetwork) {
        setNetwork(params.network);
      }
    } else {
      // URL has invalid/disabled network — redirect to first available
      const fallback = validIds.has(selectedNetwork) ? selectedNetwork : networks[0].id;
      setNetwork(fallback);
      const path = location.pathname;
      const newPath = params.network
        ? path.replace(`/${params.network}`, `/${fallback}`)
        : `/${fallback}`;
      navigate(newPath + location.search, { replace: true });
    }
  }, [params.network, networks]);

  // When network changes via selector, update the URL path
  const handleNetworkChange = (newNetwork: string) => {
    setNetwork(newNetwork);
    const path = location.pathname;
    const newPath = params.network
      ? path.replace(`/${params.network}`, `/${newNetwork}`)
      : `/${newNetwork}`;
    navigate(newPath + location.search, { replace: true });
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <Shell>
      <Overlay visible={sidebarOpen} onClick={closeSidebar} />
      <Sidebar open={sidebarOpen}>
        <LogoWrapper>
          <ClustecLogo size={32} />
          <LogoText>Clustec</LogoText>
        </LogoWrapper>
        <NetworkSelector>
          <Select
            value={selectedNetwork}
            onChange={(e) => handleNetworkChange(e.target.value)}
            style={{ width: "100%" }}
          >
            {networks?.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name}
              </option>
            ))}
          </Select>
          {analysisStatus?.running && <AnalyzingDot title="Analysis running" />}
        </NetworkSelector>
        {groups.map((group) => (
          <NavGroup key={group.heading}>
            <NavHeading>{group.heading}</NavHeading>
            {group.items.map((item) => {
              const fullPath = `/${selectedNetwork}${item.path}`;
              return (
                <NavItem
                  key={item.path}
                  to={fullPath}
                  active={location.pathname === fullPath || location.pathname === item.path}
                  onClick={closeSidebar}
                  title={item.label}
                >
                  {item.label}
                </NavItem>
              );
            })}
          </NavGroup>
        ))}
      </Sidebar>
      <Main>
        <MobileHeader>
          <HamburgerButton onClick={() => setSidebarOpen(true)}>
            &#9776;
          </HamburgerButton>
          <ClustecLogo size={24} />
          <MobileLogo>Clustec</MobileLogo>
          <MobileHeaderSpacer />
          <Search variant="mobile" />
        </MobileHeader>
        <TopBar>
          <Search />
        </TopBar>
        <Outlet />
      </Main>
      <TxClipboard />
    </Shell>
  );
}
